import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, map, mergeMap } from 'rxjs/operators';
import { RelevanceTreeActions } from './relevance-tree.actions';
import { RelevanceTreeService } from '../../service/relevanceTree.service';
import { of } from 'rxjs';
import { EvaluationTreeActions } from '../evaluation-tree/evaluation-tree.actions';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';

@Injectable()
export class RelevanceTreeEffects {

  loadRelevance$ = createEffect(
    (rs: RelevanceTreeService = inject(RelevanceTreeService)) => {
      return this.actions$.pipe(

        ofType(RelevanceTreeActions.loadRelevanceTree),
        mergeMap(({ campaignId, legalEntityId, parameters, fields, au }) =>
          rs.getRelevanceTree(legalEntityId, campaignId, parameters, au).pipe(
            map((riskMap) => {
              return RelevanceTreeActions.loadRelevanceTreeSuccess({
                riskMap: riskMap,
                fields
              });
            }),
            catchError((error) => of(RelevanceTreeActions.loadRelevanceTreeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllRelevance$ = createEffect(
    (rs: RelevanceTreeService = inject(RelevanceTreeService)) => {
      return this.actions$.pipe(

        ofType(RelevanceTreeActions.loadAllRelevanceTree),
        mergeMap(({ campaignId, legalEntityId, parameters, fields, au }) =>
          rs.getAllRelevanceTree(campaignId, legalEntityId, parameters).pipe(
            map((riskMap) => {
              return RelevanceTreeActions.loadAllRelevanceTreeSuccess({
                riskMap: riskMap,
                fields
              });
            }),
            catchError((error) => of(RelevanceTreeActions.loadAllRelevanceTreeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateRelevanceByIds$ = createEffect(
    (
      rs: RelevanceTreeService = inject(RelevanceTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(RelevanceTreeActions.updateRelevance),
        mergeMap(({ riskRelevanceTree, campaignId, legalEntityId }) =>
          rs.updateRelevanceTreeByIds(riskRelevanceTree).pipe(
            map((relevance) => {
              return RelevanceTreeActions.updateRelevanceSuccess({
                relevance
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Relevance Error',
                detail: graphQlErrorMessage(error, 'Unable to update the relevance'),
                life: 10000
              });
              return of(RelevanceTreeActions.updateRelevanceFailure({ error }));
            }),
            mergeMap((result) => [
              result,
              EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgress({ campaignId, legalEntityId })
            ])
          )
        )
      );
    },
    { functional: true }
  );


  updateNote$ = createEffect(
    (
      rs: RelevanceTreeService = inject(RelevanceTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(RelevanceTreeActions.updateTreeNote),
        mergeMap(({ campaignId, legalEntityId, processCode, cptIds, parameterName, note, filter, au, parameters, fields }) =>
          rs.updateTreeNote(campaignId, legalEntityId, processCode, note, filter).pipe(
            mergeMap((riskMap) => [
              RelevanceTreeActions.updateTreeNoteSuccess({
                riskMap,
                cptIds,
                note,
                parameterName
              }),
              RelevanceTreeActions.loadAllRelevanceTree({
                au,
                campaignId,
                legalEntityId,
                parameters,
                fields
              })
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Note Error',
                detail: graphQlErrorMessage(error, 'Unable to update the note'),
                life: 10000
              });
              return of(
                RelevanceTreeActions.updateTreeNoteFailure({
                  error,
                  cptIds,
                  parameterName
                })
              );
            })
          )
        )
      );
    },
    { functional: true }
  );

  exportRelevance$ = createEffect(
    (rs: RelevanceTreeService = inject(RelevanceTreeService)) => {
      return this.actions$.pipe(

        ofType(RelevanceTreeActions.exportRelevanceTree),
        mergeMap(({ campaignId, legalEntityId, parameters, timeZone }) =>
          rs.exportRelevanceTree(campaignId, legalEntityId, parameters, timeZone
          ).pipe(
            map((riskMap) => {
              this.downloadFile(riskMap!.data![0]!.content, riskMap!.data![0]!.filename)
              return RelevanceTreeActions.exportRelevanceTreeSuccess({
                riskMap
              });
            }),
            catchError((error) => of(RelevanceTreeActions.exportRelevanceTreeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );


  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }



  constructor(private actions$: Actions) { }
}
